import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Select, Switch, Tooltip, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import {
    BenchmarkRequestError, DimensionType, OutcomeAssociationResponse, OutcomeAssociationRow, RunOutcome,
    benchmarkService
} from '../../../services/benchmarkService';
import { NotComputed, PanelError, PanelSkeleton, SectionCard } from '../BenchmarkPrimitives';
import { count, decimal, multiplier, percent } from '../BenchmarkVocabulary';
import { DIMENSION_TYPE_LABELS, DIMENSION_TYPE_PLURALS, SIDE_LABELS, producerLabel } from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

/**
 * The two sides' marks. Validated as a categorical pair against both card surfaces
 * (#112240 dark, #FFFFFF light): lightness band, chroma, CVD and normal-vision
 * separation, and contrast all pass. They are the hues the agreement chips already
 * give each side.
 */
export const SIDE_COLORS = { platform: '#EA580C', institution: '#0284C7' } as const;
const SURFACE = dsVar('surface');
const GRID = dsVar('line');

/** Phi on the axis, -1 to 1, as a left offset in percent. */
const position = (phi: number) => `${((Math.max(-1, Math.min(1, phi)) + 1) / 2) * 100}%`;

const signed = (value: number | null | undefined, places = 2) =>
    value === null || value === undefined ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(places)}`;

const interval = (ci: [number, number] | null | undefined, places = 2) =>
    ci ? `${ci[0].toFixed(places)} to ${ci[1].toFixed(places)}` : null;

/** One side's figures, for the row's tooltip and the table view. */
const sideSummary = (label: string, side: OutcomeAssociationRow['platform']) => [
    `${label}: ${percent(side.rateFlagged)} when flagged, ${percent(side.rateUnflagged)} when not`,
    `lift ${multiplier(side.lift)}, odds ratio ${decimal(side.oddsRatio, 2)}`
        + (side.oddsRatioCi ? ` (${interval(side.oddsRatioCi)})` : ''),
    `phi ${decimal(side.phi, 2)} over ${count(side.sampleSize)} cases`
].join(' · ');

const Marker: React.FC<{ phi: number; color: string; label: string }> = ({ phi, color, label }) => (
    <Box
        aria-label={`${label}: phi ${phi.toFixed(2)}`}
        sx={{
            position: 'absolute', top: '50%', left: position(phi), width: 12, height: 12,
            transform: 'translate(-50%, -50%)', borderRadius: '50%', backgroundColor: color,
            // The surface ring keeps two overlapping markers legible.
            boxShadow: `0 0 0 2px ${SURFACE}`
        }}
    />
);

/** The phi axis behind every row: hairline gridlines at -1, -0.5, 0, 0.5 and 1. */
const AxisTrack: React.FC<{ children?: React.ReactNode; labels?: boolean }> = ({ children, labels }) => (
    <Box sx={{ position: 'relative', height: labels ? 18 : 26, mx: 1 }}>
        {[-1, -0.5, 0, 0.5, 1].map((tick) => (
            <Box key={tick} sx={{
                position: 'absolute', top: 0, bottom: 0, left: position(tick), width: '1px',
                backgroundColor: tick === 0 ? dsVar('line') : GRID
            }}>
                {labels && (
                    <Typography component="span" sx={{
                        position: 'absolute', top: 0, left: 4, fontSize: '0.6875rem', color: dsVar('fg-muted'),
                        whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'
                    }}>{tick === 0 ? '0' : signed(tick, 1)}</Typography>
                )}
            </Box>
        ))}
        {children}
    </Box>
);

const Dumbbell: React.FC<{ row: OutcomeAssociationRow }> = ({ row }) => {
    const ours = row.platform.phi;
    const theirs = row.institution.phi;
    return (
        <AxisTrack>
            {ours !== null && theirs !== null && (
                <Box sx={{
                    position: 'absolute', top: '50%', height: 2, transform: 'translateY(-50%)',
                    left: position(Math.min(ours, theirs)), width: `${(Math.abs(ours - theirs) / 2) * 100}%`,
                    backgroundColor: dsVar('fg-muted')
                }} />
            )}
            {theirs !== null && <Marker phi={theirs} color={SIDE_COLORS.institution} label={SIDE_LABELS.institution} />}
            {ours !== null && <Marker phi={ours} color={SIDE_COLORS.platform} label={SIDE_LABELS.platform} />}
        </AxisTrack>
    );
};

const Legend: React.FC = () => (
    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
        {([['platform', SIDE_LABELS.platform], ['institution', SIDE_LABELS.institution]] as const).map(([key, label]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: SIDE_COLORS[key] }} />
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>{label}</Typography>
            </Box>
        ))}
    </Box>
);

const headSx = {
    fontSize: '0.6875rem', fontWeight: 600, color: dsVar('fg-muted'), textTransform: 'uppercase' as const,
    letterSpacing: '0.05em'
};
const GRID_COLUMNS = { xs: '1fr', md: 'minmax(12rem, 1.1fr) minmax(16rem, 2fr) 9rem 8rem' };

/** Rows grouped under the agent that produces them, groups in the order they first appear. */
function groupByProducer(rows: OutcomeAssociationRow[]): [string, OutcomeAssociationRow[]][] {
    const groups = new Map<string, OutcomeAssociationRow[]>();
    for (const row of rows) {
        const key = row.producerLabel ?? producerLabel(row.producer);
        const list = groups.get(key);
        if (list) list.push(row);
        else groups.set(key, [row]);
    }
    return [...groups.entries()];
}

const ChartRow: React.FC<{ row: OutcomeAssociationRow; outcome: RunOutcome }> = ({ row, outcome }) => {
    const muted = row.indicative || row.delta.phi === null;
    const tooltip = (
        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
            <Box sx={{ fontWeight: 700, mb: 0.5 }}>{row.label}</Box>
            <Box>{sideSummary(SIDE_LABELS.platform, row.platform)}</Box>
            <Box>{sideSummary(SIDE_LABELS.institution, row.institution)}</Box>
            {row.platformOnlyOutcomeRate !== null && (
                <Box>Where only the agent replay flagged: {percent(row.platformOnlyOutcomeRate)} {outcome.label.toLowerCase()} ({count(row.platformOnlyCases)} cases)</Box>
            )}
            {row.indicative && <Box sx={{ color: ink('#FCD34D') }}>Too few cases to state as a result.</Box>}
        </Box>
    );
    return (
        <Tooltip arrow placement="top" title={tooltip}>
            <Box
                data-testid={`association-${row.dimensionKey}`}
                sx={{
                    display: 'grid', gridTemplateColumns: GRID_COLUMNS, alignItems: 'center', gap: 1.5,
                    py: 0.75, px: 1, borderRadius: '6px', opacity: muted ? 0.55 : 1, cursor: 'default',
                    '&:hover': { backgroundColor: dsVar('fg-2', 0.06) }
                }}
            >
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body') }}>
                    {row.label}
                    {row.drivesDecision === false && (
                        <Box component="span" sx={{ ml: 0.75, fontSize: '0.6875rem', color: dsVar('fg-muted') }}>(compared only)</Box>
                    )}
                </Typography>
                <Dumbbell row={row} />
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontVariantNumeric: 'tabular-nums' }}>
                    {signed(row.delta.phi)}
                    {row.delta.separable && <Box component="span" sx={{ color: ink('#10B981'), ml: 0.5 }} aria-label="distinguishable from no difference">●</Box>}
                    {row.delta.ci && (
                        <Box component="span" sx={{ display: 'block', fontSize: '0.6875rem', color: dsVar('fg-muted') }}>{interval(row.delta.ci)}</Box>
                    )}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), fontVariantNumeric: 'tabular-nums' }}>
                    {percent(row.platformOnlyOutcomeRate)}
                    {row.platformOnlyCases ? (
                        <Box component="span" sx={{ display: 'block', fontSize: '0.6875rem', color: dsVar('fg-muted') }}>of {count(row.platformOnlyCases)}</Box>
                    ) : null}
                </Typography>
            </Box>
        </Tooltip>
    );
};

/** The same figures as a table: the accessible view, and the one to copy from. */
const AssociationTable: React.FC<{ rows: OutcomeAssociationRow[] }> = ({ rows }) => (
    <Box sx={{ border: `1px solid ${GRID}`, borderRadius: '8px', overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
            <Box component="thead">
                <Box component="tr">
                    {[rows[0] ? DIMENSION_TYPE_LABELS[rows[0].dimensionType] : 'Compared', 'Agent', 'Replay phi', 'Historical phi',
                        'Difference', '95% interval', 'Replay when flagged', 'Historical when flagged', 'Replay lift',
                        'Replay odds ratio', 'Cases']
                        .map((c) => <Box key={c} component="th" sx={{ ...headSx, textAlign: 'left', px: 1.25, py: 1 }}>{c}</Box>)}
                </Box>
            </Box>
            <Box component="tbody">
                {rows.map((r) => (
                    <Box key={r.dimensionKey} component="tr" sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                        {[r.label, r.producerLabel ?? producerLabel(r.producer), decimal(r.platform.phi, 2),
                            decimal(r.institution.phi, 2), signed(r.delta.phi), interval(r.delta.ci) ?? '—',
                            percent(r.platform.rateFlagged), percent(r.institution.rateFlagged),
                            multiplier(r.platform.lift), decimal(r.platform.oddsRatio, 2), count(r.delta.sampleSize)]
                            .map((v, i) => (
                                <Box key={i} component="td" sx={{ px: 1.25, py: 0.75, fontSize: '0.8125rem', color: dsVar('fg-soft'), fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
                            ))}
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

/**
 * Which risk factors (or agents, typologies, decisions) go with the cases that met an
 * outcome, such as a SAR filed, on each side.
 *
 * Agreement with your team says whether the platform flags what your analysts flagged;
 * this says whether either side's flag means anything. Each dimension is a dumbbell on
 * the phi axis: your team's association with the outcome and the platform's, paired on
 * the same cases, with the difference and its interval beside it. Ranked by the
 * platform's gain. The last column is the outcome rate where only the platform flagged:
 * the signal your record missed, judged by what the cases became.
 */
const OutcomeAssociationPanel: React.FC<{
    runKey: number;
    /** The outcome to open on: an EDD run's SAR, else the run's first. */
    preferredOutcome?: string | null;
    cohorts?: Record<string, number>;
}> = ({ runKey, preferredOutcome, cohorts }) => {
    const [outcome, setOutcome] = useState<string | null>(preferredOutcome ?? null);
    const [dimensionType, setDimensionType] = useState<DimensionType>('factor');
    const [fullEvidence, setFullEvidence] = useState(false);
    const [asTable, setAsTable] = useState(false);
    const [data, setData] = useState<OutcomeAssociationResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const hasCohort = Boolean(cohorts && cohorts.full_evidence);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        benchmarkService.getOutcomeAssociation(runKey, {
            outcome: outcome ?? undefined, dimensionType,
            cohort: fullEvidence && hasCohort ? 'full_evidence' : undefined
        })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load the outcome association.');
            });
        return () => { cancelled = true; };
    }, [runKey, outcome, dimensionType, fullEvidence, hasCohort, attempt]);

    const rows = useMemo(() => data?.dimensions ?? [], [data]);
    const grouped = useMemo(() => (dimensionType === 'factor' ? groupByProducer(rows) : [['', rows]] as [string, OutcomeAssociationRow[]][]), [rows, dimensionType]);
    const chosen = data?.outcome;

    const controls = (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {(data?.outcomes?.length ?? 0) > 1 && (
                <Select size="small" value={chosen?.key ?? ''} onChange={(e) => setOutcome(String(e.target.value))}
                    aria-label="Outcome" sx={{ fontSize: '0.8125rem', minWidth: 170 }}>
                    {data!.outcomes!.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: '0.8125rem' }}>{o.label}</MenuItem>)}
                </Select>
            )}
            <Select size="small" value={dimensionType} onChange={(e) => setDimensionType(e.target.value as DimensionType)}
                aria-label="Compare" sx={{ fontSize: '0.8125rem', minWidth: 140 }}>
                {(['factor', 'agent', 'typology', 'decision'] as DimensionType[]).map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: '0.8125rem' }}>{DIMENSION_TYPE_PLURALS[t]}</MenuItem>
                ))}
            </Select>
            {hasCohort && (
                <Tooltip arrow title="Only the cases every decision-driving agent ran on, so no figure rests on a check that was skipped or left out.">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch size="small" checked={fullEvidence} onChange={(e) => setFullEvidence(e.target.checked)} />
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>Full evidence only ({count(cohorts!.full_evidence)})</Typography>
                    </Box>
                </Tooltip>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch size="small" checked={asTable} onChange={(e) => setAsTable(e.target.checked)} />
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>Table</Typography>
            </Box>
        </Box>
    );

    let body: React.ReactNode;
    if (error) body = <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />;
    else if (!data) body = <PanelSkeleton height={320} />;
    else if (data.availability !== 'computed') body = <NotComputed detail={data.reason ?? 'This run measured no association with outcomes.'} />;
    else if (!rows.length) body = <NotComputed detail="Nothing of this kind was compared on this run." />;
    else if (asTable) body = <AssociationTable rows={rows} />;
    else {
        body = (
            <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 1.5, px: 1, pb: 0.5, alignItems: 'end' }}>
                    <Legend />
                    <AxisTrack labels />
                    <Typography sx={headSx}>Difference</Typography>
                    <Typography sx={headSx}>{chosen ? `${chosen.label}, ${SIDE_LABELS.platformOnly.toLowerCase()}` : SIDE_LABELS.platformOnly}</Typography>
                </Box>
                {grouped.map(([group, members]) => (
                    <Box key={group || 'all'} sx={{ mt: group ? 1.5 : 0 }}>
                        {group && <Typography sx={{ ...headSx, px: 1, mb: 0.5, color: dsVar('fg-2') }}>{group}</Typography>}
                        {members.map((row) => <ChartRow key={row.dimensionKey} row={row} outcome={chosen!} />)}
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <SectionCard
            title={chosen ? `What goes with ${chosen.label.toLowerCase()}` : 'Red flags against the outcome'}
            subtitle="How strongly each side's flag went with the cases that met the outcome (phi, −1 to 1), on the same cases. Ranked by the agent replay's gain over the historical record."
            icon={<InsightsOutlinedIcon fontSize="small" />}
            accent="#A855F7"
            actions={controls}
        >
            {chosen?.circularFor === 'institution' && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6, mb: 2 }}>
                    The historical association is inflated by construction: the analysts who recorded these red flags also decided
                    whether the case was {chosen.label.toLowerCase()}. Read the difference as a lower bound on what the agent replay
                    adds, and look first at the last column, which only the replay's flags can earn.
                </Typography>
            )}
            {body}
        </SectionCard>
    );
};

export default OutcomeAssociationPanel;
